import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  Meeting, MeetingsFilters, CreateMeetingPayload, UpdateMeetingPayload,
  CalendarResponse, ScheduleMeetingPayload, MeetingAttachment,
} from '../types';

interface MeetingsResponse {
  meetings: Meeting[];
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useMeetings(filters: MeetingsFilters = {}) {
  return useQuery({
    queryKey: ['meetings', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      const { data } = await api.get<MeetingsResponse>(`/api/meetings${qs ? `?${qs}` : ''}`);
      return data.meetings ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

/** Calendar meetings in a date range + the CEO's free/busy blocks (CRM 2.0). */
export function useMeetingCalendar(from: string, to: string) {
  return useQuery({
    queryKey: ['meetings', 'calendar', from, to],
    queryFn: async () => {
      const { data } = await api.get<CalendarResponse>(`/api/meetings/calendar?from=${from}&to=${to}`);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** Next upcoming meetings for the current user (backed by GET /api/meetings/upcoming). */
export function useUpcomingMeetings() {
  return useQuery({
    queryKey: ['meetings', 'upcoming'],
    queryFn: async () => {
      const { data } = await api.get<MeetingsResponse>('/api/meetings/upcoming');
      return data.meetings ?? [];
    },
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useMeetingMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['meetings'] });

  const createMeeting = useMutation({
    mutationFn: async (payload: CreateMeetingPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/meetings', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateMeeting = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateMeetingPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/meetings/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteMeeting = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/meetings/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  // CRM 2.0 — rich scheduling (attendees, provider, note, attachments).
  const scheduleMeeting = useMutation({
    mutationFn: async (payload: ScheduleMeetingPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/meetings/schedule', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createMeeting, updateMeeting, deleteMeeting, scheduleMeeting };
}

/** Upload a file to the shared /api/upload endpoint → returns an attachment. */
export async function uploadMeetingFile(file: File): Promise<MeetingAttachment> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post<{ url: string }>('/api/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return { name: file.name, url: data.url, size: file.size, type: file.type };
}
